import React, { useEffect, useState } from "react";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { connect } from "react-redux";
import { push } from "react-router-redux";

import ContextMenu from "pages/_components/ContextMenu";

import { actions as formActions } from "reducers/form";
import { actions as notificationActions } from "reducers/notification";
import { actions as multipleTransferActions } from "reducers/multiple-transfers";
import { selectors as sessionSelectors } from "reducers/session";

import { getAccountDescription } from "util/accounts";
import * as i18n from "util/i18n";
import { getMultipleTransfersI18nKey } from "util/multiple-transfers";
import { VALIDATION_ERROR } from "util/responses";
import { checkSign } from "util/transaction";

import { multipleTransfersPre } from "middleware/multiple-transfers";
import { potentialSignaturesRequest } from "middleware/transactions";
import {
    isMasspaymentsCmfApiEnabled,
    isMultipleTransferUserCancelAllowed,
    normalizeMultipleTransferLineErrorsForModal,
    sanitizeMasspaymentCmfDisplayString,
} from "util/multipleTransfersCmf";
import MultipleTransferTicketData from "../MultipleTransferTicketData";

const idActivity = "transfer.multiple.send";
const getTitle = () => getMultipleTransfersI18nKey("multiple.transfers.ticket.title");

const MULTIPLE_TRANSFERS_HOME_PATH = "/transfer/multiple";

function MultipleTransferContextMenu({
    dispatch,
    isDesktop,
    batch,
    idTransactionStatus,
    isBatchListLayout,
    signGoBackPath,
    loggedUser,
    activeEnvironment,
    bankConcepts,
    transfer,
    action,
    setActiveTransfer,
    handleDownloadVoucher,
}) {
    const [items, setItems] = useState([]);
    const [transferData, setTransferData] = useState(null);
    const [signatureData, setSignatureData] = useState(null);

    useEffect(() => {
        const getPotencialSignatures = async () => {
            if (batch?.transactionId) {
                const { type, data } = await potentialSignaturesRequest(batch?.transactionId);
                if (type === "W") {
                    dispatch(
                        notificationActions.showNotification(i18n.get("global.unexpectedError"), "error", [
                            "multipleTransfer.list",
                        ]),
                    );
                } else {
                    setSignatureData(data?.data?.potentialSignatures);
                }
            }
        };
        getPotencialSignatures();
    }, [dispatch, batch]);

    useEffect(() => {
        if (transfer) {
            const holderType =
                transfer?.cuitCuiltDestination === activeEnvironment?.clients[0]?.idClient
                    ? i18n.get("transfers.titular.same.label")
                    : i18n.get("transfers.titular.other.label");
            const lineVoucher =
                transfer.voucherNumber != null && String(transfer.voucherNumber).trim() !== ""
                    ? String(transfer.voucherNumber).trim()
                    : null;
            const batchVoucher =
                isMasspaymentsCmfApiEnabled() &&
                batch?.voucherNumber != null &&
                String(batch.voucherNumber).trim() !== ""
                    ? String(batch.voucherNumber).trim()
                    : null;
            const voucherResolved = lineVoucher ?? batchVoucher ?? "";
            const base = {
                transferDate: {
                    value: batch.processDate,
                    format: "datetime",
                },
                voucherNumber: {
                    value: voucherResolved,
                    format: "string",
                },
                originName: {
                    value: activeEnvironment?.name,
                    format: "string",
                },
                debitAccount: {
                    value: getAccountDescription(batch.account),
                    format: "string",
                },
                transferType: {
                    value: holderType,
                    format: "string",
                },
                cbuDestination: {
                    value: transfer.cbuDestination,
                    format: "string",
                },
                cuitCuiltDestination: {
                    value: transfer?.cuitCuiltDestination,
                    format: "string",
                },
                holder: {
                    value: isMasspaymentsCmfApiEnabled()
                        ? sanitizeMasspaymentCmfDisplayString(transfer.destinationName)
                        : transfer.destinationName,
                    format: "string",
                },
                bankName: {
                    value: isMasspaymentsCmfApiEnabled()
                        ? sanitizeMasspaymentCmfDisplayString(transfer.bankName)
                        : transfer.bankName,
                    format: "string",
                },
                transferAmount: {
                    value: { quantity: transfer.amount, currency: batch.amount?.currency || "ARS" },
                    format: `amount${i18n.get(`currency.label.${batch.amount?.currency || "ARS"}`)}`,
                },
                concept: {
                    value: `${transfer.idBankConcepts} - ${bankConcepts[transfer.idBankConcepts]}`,
                    format: "string",
                },
                transferReference: {
                    value: transfer.reference,
                    format: "string",
                },
            };
            if (isMasspaymentsCmfApiEnabled() && transfer.status === "ERROR" && transfer.error) {
                const normalized = normalizeMultipleTransferLineErrorsForModal(transfer.error);
                const first = normalized && normalized.length > 0 ? normalized[0] : { code: "", description: "" };
                if (first.code != null && String(first.code).trim() !== "") {
                    base.errorCode = {
                        value: String(first.code).trim(),
                        format: "string",
                    };
                }
                if (first.description != null && String(first.description).trim() !== "") {
                    base.errorDescription = {
                        value: String(first.description).trim(),
                        format: "string",
                    };
                }
            }
            setTransferData(base);
        }
        // eslint-disable-next-line
    }, [batch, transfer]);

    let batchData = null;

    if (batch) {
        batchData = {
            id: {
                value: batch.idBatch,
                format: "string",
            },
            debitAccount: {
                value: getAccountDescription(batch.account),
                format: "string",
            },
            reference: {
                value: batch.reference,
                format: "string",
            },
            uploadDate: {
                value: batch.creationDate,
                format: "datetime",
            },
            executionDate: {
                value: batch.processDate,
                format: "date",
            },
            fileName: {
                value: batch.fileName,
                format: "string",
            },
            quantity: {
                value: batch.quantity,
                format: "string",
            },
            amount: {
                value: batch.amount?.quantity,
                format: `amount${i18n.get(`currency.label.${batch.amount?.currency || "ARS"}`)}`,
            },
            ...(isMasspaymentsCmfApiEnabled() &&
            batch.voucherNumber != null &&
            String(batch.voucherNumber).trim() !== ""
                ? {
                      voucherNumber: {
                          value: String(batch.voucherNumber).trim(),
                          format: "string",
                      },
                  }
                : {}),
        };
    }

    const cancelTransaction = () => {
        const cancelTransactionData = (
            <MultipleTransferTicketData
                dispatch={dispatch}
                isDesktop={isDesktop}
                data={batchData}
                batch={{ ...batch, idTransactionStatus: batch?.batchStatus }}
            />
        );
        dispatch(push({ pathname: "/transactions/cancel", state: { goBackState: MULTIPLE_TRANSFERS_HOME_PATH } }));
        dispatch(
            formActions.cancelTransactionData({
                idTransaction: batch?.transactionId,
                content: cancelTransactionData,
                title: getTitle(),
                force: true,
            }),
        );
        dispatch(formActions.cancelTransactionPre({ idActivity, idForm: "" }));
    };

    const signTransaction = () => {
        const signTransactionData = (
            <MultipleTransferTicketData
                dispatch={dispatch}
                isDesktop={isDesktop}
                data={batchData}
                batch={{ ...batch, idTransactionStatus: batch?.batchStatus }}
            />
        );

        const goBackStateForSign = isBatchListLayout
            ? MULTIPLE_TRANSFERS_HOME_PATH
            : signGoBackPath || MULTIPLE_TRANSFERS_HOME_PATH;
        dispatch(push({ pathname: "/transactions/sign", state: { goBackState: goBackStateForSign } }));
        dispatch(
            formActions.signTransactionData({
                idTransaction: batch?.transactionId,
                idActivity,
                idForm: "",
                content: signTransactionData,
                title: getTitle(),
                pendingDispatch: false,
            }),
        );

        dispatch(
            formActions.signTransactionPreview({
                idActivity,
                idTransaction: batch?.transactionId,
                idForm: "",
                ticketData: batchData,
            }),
        );
    };

    const validateSignTransaction = async () => {
        dispatch(multipleTransferActions.setFetching(true));
        const { type, data } = await multipleTransfersPre();
        if (type === "W") {
            const { NO_FIELD } = data.data;
            if (data.code === VALIDATION_ERROR) {
                dispatch(notificationActions.showNotification(NO_FIELD, "warning", ["multipleTransfer.list"]));
            }
        } else {
            signTransaction();
        }
        dispatch(multipleTransferActions.setFetching(false));
    };

    useEffect(() => {
        switch (idTransactionStatus) {
            case "PENDING_SEND":
            case "PENDING_VALIDATION":
            case "PENDING":
                if (isBatchListLayout) {
                    const signedTransaction = checkSign({ signatures: batch.signatures }, loggedUser, signatureData);
                    const itemsPending = [];
                    if (isMultipleTransferUserCancelAllowed(batch)) {
                        itemsPending.push({
                            label: "multiple.transfers.card.cancel",
                            onClick: () => {
                                cancelTransaction();
                            },
                            icon: "CancelList",
                        });
                    }
                    if (signedTransaction && idTransactionStatus === "PENDING") {
                        itemsPending.push({
                            label: "multiple.transfers.card.sign",
                            onClick: () => {
                                validateSignTransaction();
                            },
                            icon: "Sign",
                        });
                    }
                    setItems(itemsPending);
                }
                break;
            case "SUCCESS":
                if (!isBatchListLayout && (!isMasspaymentsCmfApiEnabled() || transferData)) {
                    setItems([
                        {
                            label: "multiple.transfers.detail.context.ticket",
                            onClick: () => {
                                setActiveTransfer(transferData);
                            },
                            icon: "Eye",
                        },
                        {
                            label: "multiple.transfers.detail.context.download",
                            onClick: () => {
                                handleDownloadVoucher(transferData, false);
                            },
                            icon: "SendDown",
                        },
                    ]);
                }
                break;
            case "ERROR":
                if (!isBatchListLayout && transfer.error && (!isMasspaymentsCmfApiEnabled() || transferData)) {
                    if (isMasspaymentsCmfApiEnabled()) {
                        setItems([
                            {
                                label: "multiple.transfers.detail.context.ticket",
                                onClick: () => {
                                    if (setActiveTransfer) {
                                        setActiveTransfer({ ...transferData, status: transfer.status });
                                    }
                                },
                                icon: "Eye",
                            },
                        ]);
                    } else {
                        setItems([
                            {
                                label: "multiple.transfers.detail.context.error",
                                onClick: () => {
                                    action();
                                },
                                icon: "Eye",
                            },
                        ]);
                    }
                }
                break;

            default:
                break;
        }
        // eslint-disable-next-line
    }, [dispatch, idTransactionStatus, isBatchListLayout, signatureData, transfer, transferData, handleDownloadVoucher]);

    return <ContextMenu imageStyle="pr-0" items={items} />;
}

MultipleTransferContextMenu.propTypes = {
    dispatch: func.isRequired,
    setActiveTransfer: func,
    handleDownloadVoucher: func,
    isDesktop: bool.isRequired,
    batch: shape({}).isRequired,
    idTransactionStatus: string.isRequired,
    isBatchListLayout: bool.isRequired,
    signGoBackPath: string,
    loggedUser: shape({
        userId: string.isRequired,
    }).isRequired,
    activeEnvironment: shape({}).isRequired,
    bankConcepts: arrayOf(string),
    transfer: shape({}),
    action: func,
};

MultipleTransferContextMenu.defaultProps = {
    handleDownloadVoucher: undefined,
    setActiveTransfer: undefined,
    bankConcepts: [],
    transfer: null,
    action: null,
    signGoBackPath: undefined,
};

const mapStateToProps = (state) => ({
    loggedUser: sessionSelectors.getUser(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
});

export default connect(mapStateToProps)(MultipleTransferContextMenu);
